<script setup lang="ts">
/**
 * 答えの選択肢を表示するための専門コンポーネント。
 * 親から選択肢のリストをもらって、どれが選ばれたかを親に伝える
 */
import type { Choice } from '@/types';
import { useI18n } from 'vue-i18n';

// 親コンポーネントから渡されるデータ（プロパティ）の定義
defineProps<{
  /**
   * 選択肢の配列。
   * [{ id: 1, text: 'いぬ' }, ...] のような形式を期待してる
   */
  choices: Choice[]
}>();

const { locale } = useI18n();

// 子コンポーネントから親コンポーネントへイベントを伝えるための`emit`を定義する
const emit = defineEmits<{
  (e: 'select-choice', choiceId: number): void
}>();

/**
 * 選択肢ボタンがクリックされたときに呼ばれる関数だ
 * @param {number} choiceId クリックされた選択肢のID
 */
const onSelect = (choiceId: number) => {
  // 'select-choice' という名前のイベントを発生させて、親に選ばれた選択肢のIDを伝える
  emit('select-choice', choiceId);
};

const getChoiceText = (choice: Choice) => {
  if (choice.translations) {
    // Current locale match
    if (choice.translations[locale.value]) {
      return choice.translations[locale.value].text;
    }
    // Fallback to English if current is Japanese but missing (unlikely) or vice versa
    if (locale.value === 'ja' && choice.translations['en']) {
      // Just fallback to default text which is likely Japanese if source is Japanese
    }
    // Try 'en' if available and we are not in 'en' (or vice versa, but mainly use locale)
  }
  return choice.text;
};
</script>

<template>
  <!-- 選択肢ボタン全体を囲むコンテナ -->
  <div class="choices-container">
    <!-- v-forで選択肢の配列をループして、ボタンを一個ずつ生成する -->
    <button v-for="choice in choices" :key="choice.id" @click="onSelect(choice.id)" class="choice-button">
      {{ getChoiceText(choice) }}
    </button>
  </div>
</template>

<style scoped>
.choices-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 320px;
  /* 350px -> 320px */
  max-width: 85vw;
}

@media (min-width: 600px) {
  .choices-container {
    width: 480px;
  }
}

@media (min-width: 1024px) {
  .choices-container {
    width: 350px;
    /* PCでは横並びになるので、幅はそこそこでよい */
  }
}

.choice-button {
  padding: 1rem;
  font-size: 1.2rem;
  cursor: pointer;
  border: none;
  background-color: #fff;
  border: 2px solid #ddd;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.choice-button:hover {
  background-color: #f0f0f0;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
